import { observable } from 'mobx'
import type { LanguageLocale, LanguageCode } from 'utils/language'
import type { StoreFrontResponse } from 'mobx/AddressManager/types'

export type StorageType = 'local' | 'session'

export interface Configuration {
	orderTypeFlows: OrderTypeFlow[]
	canSkipOrderTypeSelection: boolean
	displayToMenuButton: boolean
  	savedAddressesEnabled?: boolean
	enabled: boolean
	storageType: StorageType
}

export interface Localization {
	actualFlow: LocalizationFlow | null | undefined
	processingFlow: LocalizationFlow | null | undefined

	orderTypeFlows: OrderTypeFlow[]
	setOrderTypeFlows: (orderTypeFlows: OrderTypeFlow[]) => void

	savedAddresses: SavedAddress[]
	savedAddressesFetched: boolean
	fetchSavedAddresses: () => Promise<void>
	clearSavedAddresses: () => void
	addSavedAddress: (flow: LocalizationFlow) => Promise<SavedAddress | undefined>
	editSavedAddress: (flow: LocalizationFlow) => Promise<void>
	removeSavedAddress: (addressId: string) => Promise<void>
	reachedMaximumSavedAddresses: boolean

	started: boolean | undefined
	currentStep: FlowStep | null
	alreadyLocalized: boolean | undefined

	start: (preSelectedStore: Store | null) => Promise<void> | undefined
	abort: () => void
	end: () => void
	nextStep: () => void
	previousStep: () => void
	goToStep: (step: FlowStep) => void
	canGoToStep: (step: FlowStep) => boolean

	getValidateAddress(address: NonSavedAddress): Promise<any> | undefined
	goToLocalizedMenu: () => void
	getLocalizedMenuPath: () => string | undefined

	config: () => Configuration | undefined

	savedAddressesEnabled: boolean

	forceLocalizationToStore: (storeId: string) => void

	getAddressFromText: (addressText: string) => Promise<NonSavedAddress> | undefined

	matchAndSetStoreForAddress: (address: NonSavedAddress) => Promise<void>

	sendLocalizationEvent(eventName: string, params?: any): void
}

export class LocalizationFlow {
	constructor() {}

	orderTypeFlow: OrderTypeFlow | null = null

	setOrderTypeFlow = (orderTypeFlow: OrderTypeFlow) => {
		const previousDisplayAvailableStores = this.orderTypeFlow?.displayAvailableStores
		this.orderTypeFlow = {
			...orderTypeFlow,
			displayAvailableStores: previousDisplayAvailableStores,
		}
	}

	setOrderTypeFlowDisplayAvailableStores = (displayAvailableStores: boolean) => {
		if (this.orderTypeFlow) {
			this.orderTypeFlow = {
				...this.orderTypeFlow,
				displayAvailableStores,
			}
		}
	}

	store: Store | null = null

	setStore = (store: Store) => {
		this.store = store
	}

	resetStore = () => {
		this.store = null
	}

	address: NonSavedAddress | null = null

	setAddress = (address: NonSavedAddress) => {
		this.address = address
	}

	clearAddress = () => {
		this.address = null
		this.savedAddressId = null
	}

	setAddressLocation = (formatted: string, location: Location) => {
		if (this.address) {
			this.address = {
				...this.address,
				formatted,
				location,
			}
		}
	}

	savedAddressId: string | null = null

	get usingSavedAddress() {
		return !!this.savedAddressId
	}

	selectSavedAddress = ({ _id, formatted, location, components, locationDetails, label, ...rest }: SavedAddress) => {
		this.address = { formatted, location, components }
		this.savedAddressId = _id
		this.locationDetails = locationDetails || null

		this.savingAddressData = {
			label,
			customLabel: (rest as OtherSavedAddress).customLabel
		}
	}

	unselectSavedAddress = () => {
		this.savedAddressId = null
	}

	setSavedAddressId = (addressId: string | null) => {
		this.savedAddressId = addressId
	}

	locationDetails: LocationDetails | null = null

	setLocationDetails = (details: LocationDetails | null) => {
		this.locationDetails = details
	}

	@observable savingAddressData: SavingAddressData | null = null
	
	setSavingAddressLabel = (label: AddressLabel) => {
		this.savingAddressData = {
			label,
		}
	}
	
	setSavingAddressCustomLabel = (customLabel: string) => {
		if (this.savingAddressData) {
			this.savingAddressData.customLabel = customLabel
		}
	}
	
	startSavingAddress = () => {
		this.savingAddressData = {
			label: AddressLabel.Home,
		}
		
	}
	
	stopSavingAddress = () => {
		this.savingAddressData = null
	}
}

interface GetStoresResponse {
	shouldGoToFirstStoreFound: boolean
	stores: StoreResponse[]
}

interface StoreResponseeAvailability {
	availableNow: boolean
	canPreOrder: boolean
	canOrderNowOrPreOrder: boolean
	until: Partial<Record<LanguageCode, {message: string, timestamp: number, reopensTimestamp: any}>>,
	availableForAddress: boolean
	hours: Partial<Record<LanguageCode, string[][]>>[],
}

export interface StoreResponse {
	id: string
	title: Partial<Record<LanguageLocale, string>>
	address: {
		formatted: string
		city: string
		latLng: {
			lat: number
			lng: number
		},
		countryCode: string
		properties: Partial<Record<string, string>>
	}
	distanceNum: number
	distance: string
	contact: {
		email: string
		phone: string
	}
	openingHours: Partial<Record<LanguageCode, string[][]>>[],
	timezone: string
	pickUpAllwed: boolean
	deliveryAllowed: boolean
	availability: {
		delivery: StoreResponseeAvailability,
		pickup: StoreResponseeAvailability,
		store: StoreResponseeAvailability,
		preOrder: boolean,
		open: boolean,
		orderOnline: boolean,
		supportedOrderTypes: string[],
		nearby: boolean,
		hour12: boolean
	},
	description: Partial<Record<LanguageLocale, string>>
}

export interface LocalizationDependencies {
	getStores: () => Promise<{ deliveryStores: boolean; disablePickup: boolean; pickupStores: [] }>
	getStoresOnAddress: (address: NonSavedAddress) => Promise<GetStoresResponse>

	clearCouponToApply: () => void

	// Once we move the stores to be on the localizationStore, it should be there
	updateMapStores: (stores: any[]) => void

	fetchSavedAddresses: () => Promise<SavedAddress[]>
	createSavedAddress: (flow: LocalizationFlow) => Promise<SavedAddress>
	updateSavedAddress: (flow: LocalizationFlow) => Promise<void>
	deleteSavedAddress: (id: string) => Promise<void>

	save(flow: LocalizationFlow, storageType?: StorageType): void
	get(storageType?: StorageType): LocalizationFlow | null
	config(): Configuration | undefined

	initSessionAndLoadMenu(storeId: string): Promise<boolean>
	initSession(storeId?: string): Promise<string>

	getValidateAddress(address: NonSavedAddress): Promise<any>

	goToLocalizedMenu: () => Promise<void>
	getLocalizedMenuPath: () => string | undefined

	getStoreDetailsById(storeId: string): Promise<StoreFrontResponse>
	checkPickupAvailabilityByStore(store: StoreFrontResponse): void

	sendLocalizationEvent(eventName: string, params?: any): void

	emitAddressSavedEvent(savedAddress: SavedAddress): void
	emitAddressFailedToSaveEvent(error: unknown): void
	emitOrderTypeFlowsUnavailable(): void

	getGeoCodeFromAddressText(address: string): Promise<google.maps.GeocoderResult>
}

export interface OrderTypeFlow {
	orderType: OrderType
	orderTypeTranslationKey: string
	orderTypeImageSrc: string
	steps: FlowStep[]
	requireAddress: boolean
	displayAvailableStores?: boolean
}

export interface Store {
	id: string
	name: Partial<Record<LanguageLocale, string>>
	address: string
}

export enum FlowStep {
	OrderTypeSelection = 'OrderTypeSelection',
	AddressSelection = 'AddressSelection',
	LocationDetailsSelection = 'LocationDetailsSelection',
	StoreOnMapSelection = 'StoreOnMapSelection',
}

export interface NonSavedAddress {
	formatted: string
	location: Location
	components: google.maps.GeocoderAddressComponent[]
}

export interface BaseSavedAddress extends NonSavedAddress {
	_id: string
	label: AddressLabel
	locationDetails?: LocationDetails
}

export type SavedAddress = HomeSavedAddress | WorkSavedAddress | OtherSavedAddress

export interface HomeSavedAddress extends BaseSavedAddress {
	label: AddressLabel.Home
}

export interface WorkSavedAddress extends BaseSavedAddress {
	label: AddressLabel.Work
}

export interface OtherSavedAddress extends BaseSavedAddress {
	label: AddressLabel.Other
	customLabel: string
}

export interface Location {
	lat: number
	lng: number
}

export enum OrderType {
	Delivery = 'delivery',
	Pickup = 'pickup',
}

export enum AddressLabel {
	Home = 'home',
	Work = 'work',
	Other = 'other'
}

export interface SavingAddressData {
	label: AddressLabel
	customLabel?: string
}


interface LocationDetails {
	type: LocationType,
	informations: Field[] | undefined
}

export enum LocationType {
	House = 'house',
	Office = 'office',
	Other = 'other',
	Appartment = 'appartment'
}

export interface Field {
	name: string
    label: string
    translatedKey: string
	value: string | null
}

export type LocationsDetails = {
    [key in LocationType]?: Field[]
}

// Need to check if this should be saved in database, or hardcoded
export const defaultLocationsDetails : LocationsDetails = {
    [LocationType.Appartment]: [
        {name: 'floor', label: 'Floor', translatedKey: 'xxx'},
        {name: 'aptNumber', label: 'Apt number', translatedKey: 'xxx'},
        {name: 'deliveryNotes', label: 'Notes to delivery person', translatedKey: 'xxx'}
    ] as Field[],
    [LocationType.Office]: [
        {name: 'floor', label: 'Floor', translatedKey: 'xxx'},
        {name: 'companyNane', label: 'Company name', translatedKey: 'xxx'},
        {name: 'deliveryNotes', label: 'Notes to delivery person', translatedKey: 'xxx'}
    ] as Field[],
    [LocationType.House]: [
        {name: 'doorBell', label: 'Doorbell', translatedKey: 'xxx'},
        {name: 'deliveryNotes', label: 'Notes to delivery person', translatedKey: 'xxx'}
    ] as Field[],
    [LocationType.Other]: [
        {name: 'locationType', label: 'Type of location', translatedKey: 'xxx'},
        {name: 'deliveryNotes', label: 'Notes to delivery person', translatedKey: 'xxx'}
    ] as Field[],
}
